import { ReactComponent as EmptyPanelDroneIcon } from '@assets/components/panel/notification/empty-panel-drone-icon.svg';
import { IcClose } from '@assets/icons/24x24';
import { Center, Dialog, For, HStack, Icon, IconButton, Separator, Tabs, Text, VStack } from '@chakra-ui/react';
import { ScrollArea } from '@components/ui/ScrollArea';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasRole } from '@redux/authent/authent.selectors';
import { selectPlatformByCode } from '@redux/config/config.selectors';
import { FormattedMessage, useIntl } from 'react-intl';

import { RoleEnum } from '@/types/authent/roles.types';
import { MessageRequest } from '@/types/chat/chat.types';
import { AlertType, NotificationTypeEnum } from '@/types/data/data.types';

import Alert from './Alert';
import { useAlertDetailsContext } from './AlertsDetail';
import Message from './Message';

function EmptyList({ label }: Readonly<{ label: string }>) {
  return (
    <Center width="100%" height="430px" backgroundColor="neutral.black">
      <VStack gap={2} width="100%">
        <Icon as={EmptyPanelDroneIcon} boxSize="80px" />
        <Text color="neutral.200">
          <FormattedMessage id={label} />
        </Text>
      </VStack>
    </Center>
  );
}

function AlertList({ alerts, maxHeight }: Readonly<{ alerts: AlertType[] | undefined; maxHeight?: number }>) {
  return (
    <VStack width="100%" gap={0.25} maxHeight={maxHeight}>
      <For each={alerts} fallback={<EmptyList label="components.notification.detail.platforms.emptyAlert" />}>
        {(alert) => <Alert key={alert.id} alert={alert} />}
      </For>
    </VStack>
  );
}

function MessageList({ messages }: Readonly<{ messages: MessageRequest[] | undefined }>) {
  return (
    <VStack width="100%" gap={0.25}>
      <For each={messages} fallback={<EmptyList label="components.notification.detail.platforms.emptyMessage" />}>
        {(message) => <Message key={message.id} message={message} />}
      </For>
    </VStack>
  );
}

export function AlertModal({ type }: Readonly<{ type: NotificationTypeEnum }>) {
  const { formatMessage } = useIntl();
  const { detailsByPlatform, selectedPlatform, open, setOpen } = useAlertDetailsContext();
  const platform = useAppSelector((state) => selectPlatformByCode(state, selectedPlatform ?? ''));
  const platformName = platform?.name ?? formatMessage({ id: 'notification.message.unknownPlatform' });
  const canSeeMessages = useAppSelector((state) => hasRole(state, RoleEnum.ACCESS_MESSAGE_REQUEST));
  const detail = selectedPlatform === null ? null : detailsByPlatform.get(selectedPlatform);

  return (
    <Dialog.Root
      open={open}
      unmountOnExit
      lazyMount
      placement="top"
      onOpenChange={(details) => {
        if (!details.open) {
          setOpen(false);
        }
      }}
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content userSelect="none" marginTop={150}>
          <VStack backgroundColor="neutral.black" width="464px" border="2px solid" borderColor="neutral.black" gap={0}>
            <HStack width="100%" backgroundColor="neutral.800" justifyContent="flex-end">
              <Text textTransform="uppercase" marginLeft={2} marginRight="auto" color="neutral.200">
                {platformName}
              </Text>
              <IconButton
                variant="transparent"
                boxSize="40px"
                aria-label="cancel"
                onClick={() => {
                  setOpen(false);
                }}
              >
                <Icon as={IcClose} boxSize="24px" />
              </IconButton>
            </HStack>
            <Separator borderColor="neutral.900" />
            {type === NotificationTypeEnum.DRONE && canSeeMessages ? (
              <Tabs.Root variant="neutral" gap={0.25} defaultValue="alerts" lazyMount>
                <Tabs.List>
                  <Tabs.Trigger value="alerts">
                    <HStack gap={1} justifyContent="center">
                      <Text>
                        <FormattedMessage id="components.notification.detail.platforms.alerts" />
                      </Text>
                      <Text color="skyblue">{detail?.alerts.length}</Text>
                    </HStack>
                  </Tabs.Trigger>
                  <Tabs.Trigger value="messages">
                    <HStack gap={1} justifyContent="center">
                      <Text>
                        <FormattedMessage id="components.notification.detail.platforms.messages" />
                      </Text>
                      <Text color="skyblue">{detail?.messages.length}</Text>
                    </HStack>
                  </Tabs.Trigger>
                </Tabs.List>
                <Tabs.ContentGroup>
                  <ScrollArea backgroundColor="neutral.850">
                    <Tabs.Content value="alerts" maxHeight={550}>
                      <AlertList alerts={detail?.alerts} />
                    </Tabs.Content>
                    <Tabs.Content value="messages" maxHeight={550} backgroundColor="neutral.850">
                      <MessageList messages={detail?.messages} />
                    </Tabs.Content>
                  </ScrollArea>
                </Tabs.ContentGroup>
              </Tabs.Root>
            ) : (
              <ScrollArea backgroundColor="neutral.850">
                <AlertList alerts={detail?.alerts} maxHeight={550} />
              </ScrollArea>
            )}
          </VStack>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
